import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (password: string, confirm: string) => Promise<void>;
  mode?: 'setup' | 'change';
};

export function SetupNotesLockModal({ open, onClose, onSubmit, mode = 'setup' }: Props) {
  const { t } = useTranslation('settings');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [current, setCurrent] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const isChange = mode === 'change';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isChange) {
        if (password !== confirm) throw new Error(t('notesLock.errors.mismatch'));
        await onSubmit(current, password);
      } else {
        await onSubmit(password, confirm);
      }
      setPassword('');
      setConfirm('');
      setCurrent('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('notesLock.errors.setupFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-xl bg-primary/10 p-2 text-primary">
            <Lock size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">
              {isChange ? t('notesLock.changeTitle') : t('notesLock.setupTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isChange ? t('notesLock.changeDescription') : t('notesLock.setupDescription')}
            </p>
          </div>
        </div>
        {isChange ? (
          <>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('notesLock.currentPasswordLabel')}
            </label>
            <input
              type="password"
              autoFocus
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mb-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </>
        ) : null}
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {isChange ? t('notesLock.newPasswordLabel') : t('notesLock.passwordLabel')}
        </label>
        <input
          type="password"
          autoFocus={!isChange}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('notesLock.confirmPasswordLabel')}
        </label>
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="mb-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
        {error ? <p className="mb-3 text-sm text-red-500">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/50">
            {t('notesLock.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !password || !confirm || (isChange && !current)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy ? t('notesLock.saving') : isChange ? t('notesLock.changePassword') : t('notesLock.setPassword')}
          </button>
        </div>
      </form>
    </div>
  );
}
